import { useEffect, useRef, useState } from "react";
import { apiGet, ApiError } from "./client";

export interface PollState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** True once at least one successful response has landed. */
  everSucceeded: boolean;
}

/**
 * Poll a GET endpoint on an interval, with graceful degradation: a failure
 * keeps the last-known-good `data` on screen and surfaces `error` for a
 * banner, rather than unmounting the UI (BUILD THIS #9 — no white screen of
 * death when the backend is down).
 */
export function usePolling<T>(path: string, intervalMs: number, enabled = true): PollState<T> {
  const [state, setState] = useState<PollState<T>>({
    data: null,
    error: null,
    loading: true,
    everSucceeded: false,
  });
  const everSucceededRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const controller = new AbortController();

    async function tick() {
      try {
        const data = await apiGet<T>(path, controller.signal);
        if (cancelled) return;
        everSucceededRef.current = true;
        setState({ data, error: null, loading: false, everSucceeded: true });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : String(e);
        setState((prev) => ({
          data: prev.data,
          error: msg,
          loading: false,
          everSucceeded: everSucceededRef.current,
        }));
      }
    }

    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, intervalMs, enabled]);

  return state;
}
